import map from "lodash/map";
import merge from "lodash/merge";
import moment from "moment";
import { Gantt } from "PFApp/components/gantt";
import { useRoleJobCode } from "PFApp/use_role_job_code";
import { useMyAccountInfo } from "PFCore/hooks/queries/account/use_my_account_info";
import { useProfilesBookings } from "PFCore/hooks/queries/profile/use_profiles_bookings";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { AvailabilityRule } from "PFTypes";
import { useMemo } from "react";

import { ComparableProfile } from "../details/parts/use_cell_data";
import { AvailabilityInfoPanel } from "./availability_info_panel";
import { ProfileLabel } from "./profile_label";

type CompareAvailabilityPageProps = {
  comparableProfiles: ComparableProfile[];
  startDate: string;
  availabilityInfo: AvailabilityRule | null;
  removeProfileId: (id: number) => void;
  isUnbiasedMode?: boolean;
};

export const CompareAvailabilityPage = ({
  comparableProfiles,
  startDate,
  availabilityInfo,
  removeProfileId,
  isUnbiasedMode
}: CompareAvailabilityPageProps) => {
  const {
    data: { availability }
  } = useMyAccountInfo();
  const jobCodeDisplayAs = useRoleJobCode();
  const { formatISODate } = useDateFormatter();

  const monthsLimit = useMemo(() => availability?.calculated_months_limit || 6, [availability]);

  const endDate = useMemo(
    () => formatISODate(moment(startDate).add(monthsLimit, "months").startOf("isoWeek").set("hour", 12)),
    [startDate, monthsLimit, formatISODate]
  );

  const { bookingsByProfileId } = useProfilesBookings({
    profiles: map(comparableProfiles, ({ profile }) => ({ id: profile.id, rowKey: String(profile.id) })),
    dateRange: { min: startDate, max: endDate }
  });

  const rowItems: ComparableProfile[] = useMemo(
    () =>
      map(comparableProfiles, (comparableProfile) => {
        const { profile } = comparableProfile;

        return merge({}, comparableProfile, {
          profile: {
            availability: {
              bookings: bookingsByProfileId[profile.id] || []
            }
          }
        });
      }),
    [bookingsByProfileId, comparableProfiles]
  );

  return (
    <Gantt
      monthsLimit={monthsLimit}
      rowItems={rowItems}
      startDate={startDate}
      availabilityInfo={availabilityInfo as { start?: string; end?: string }}
      infoPanel={<AvailabilityInfoPanel availability={availabilityInfo} />}
      rowItemLabelComponent={(shortlist) => (
        <ProfileLabel
          key={shortlist.id}
          shortlist={shortlist}
          removeProfileId={removeProfileId}
          isUnbiasedMode={isUnbiasedMode}
        />
      )}
      jobCodeDisplayAs={jobCodeDisplayAs}
    />
  );
};
